import { cn } from '@/utils';
import { ScoreProcessingCard } from '@/components/Score';
import { TextContent } from '@/components/Text';
import { WORDS, STATUS_MESSAGES } from '@/constants';

/** Props for the SimulationScoreProcessingView component */
interface SimulationScoreProcessingViewProps {
  /** The title of the certification */
  certificationTitle?: string;
  /** Indicates if the component is in a loading state */
  isLoading?: boolean;
  /** Indicates if the screen size is extra large or larger for responsive styling */
  isXlUp?: boolean;
  /** Handler for navigating back to the dashboard */
  onGoToDashboard: () => void;
  /** Handler for retaking the roleplay */
  onRetakeRoleplay: () => void;
  /** Optional description to display while score is processing */
  scoreProgessingDescription?: string;
  /** Optional progress value (0-100) to indicate score processing progress */
  scoreProcessingProgress?: number;
  /** The title of the simulation */
  simulationTitle?: string;
}

/**
 * View component for displaying the score processing state after a simulation
 * roleplay is completed. Shows a loading state while the score is being processed
 * and provides options to navigate back to the dashboard or retake the roleplay.
 */
export const SimulationScoreProcessingView: React.FC<SimulationScoreProcessingViewProps> = ({
  certificationTitle,
  isLoading = false,
  isXlUp = false,
  onGoToDashboard,
  onRetakeRoleplay,
  scoreProgessingDescription = STATUS_MESSAGES.PROCESSING_SCORE_DESCRIPTION,
  scoreProcessingProgress,
  simulationTitle,
}) => {
  return (
    <div className="w-full max-w-3xl">
      <div className="mb-8 w-full">
        <div className="mb-6 flex w-full items-center justify-between">
          <TextContent
            className="!font-display text-body-title font-bold text-neutral-gray-800"
            isLoading={isLoading}
            label={certificationTitle}
            loadingClassName="w-1/3 h-[1.25rem]"
            type="paragraph"
          />
        </div>

        <TextContent
          isLoading={isLoading}
          label={`${simulationTitle} ${WORDS.SCORECARD}`}
          level={1}
          loadingClassName={cn('w-full h-[4rem]', isLoading ? (isXlUp ? '' : 'mt-0.5') : '')}
          loadingLines={isXlUp ? 1 : 2}
          type="headline"
          variant="pageTitle"
        />
      </div>

      <ScoreProcessingCard
        description={scoreProgessingDescription}
        onGoToDashboard={onGoToDashboard}
        onRetakeRoleplay={onRetakeRoleplay}
        progress={scoreProcessingProgress}
      />
    </div>
  );
};

SimulationScoreProcessingView.displayName = 'SimulationScoreProcessingView';

export default SimulationScoreProcessingView;
